import React, { useEffect, useState, useCallback } from "react";
import aiAutomationApi from "../../utils/aiAutomationApi";

// ─── helpers ─────────────────────────────────────────────────────────────────
const clamp = (v) => Math.min(100, Math.max(0, Number(v) || 0));
const activeTotal = (criteria) =>
  criteria.filter((c) => c.is_active).reduce((s, c) => s + Number(c.weight), 0);

// ─── icons ────────────────────────────────────────────────────────────────────
const SliderIcon = () => (
  <svg className="w-5 h-5 mr-2 inline-block" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <line x1="4" y1="6" x2="20" y2="6" strokeLinecap="round" />
    <circle cx="9" cy="6" r="2" fill="white" stroke="currentColor" strokeWidth="1.5" />
    <line x1="4" y1="12" x2="20" y2="12" strokeLinecap="round" />
    <circle cx="15" cy="12" r="2" fill="white" stroke="currentColor" strokeWidth="1.5" />
    <line x1="4" y1="18" x2="20" y2="18" strokeLinecap="round" />
    <circle cx="10" cy="18" r="2" fill="white" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5h6v2M10 11v6M14 11v6M5 7l1 13h12l1-13" />
  </svg>
);

// ─── Built-in criteria keys (cannot be deleted) ───────────────────────────────
const BUILTIN = new Set(["skill", "experience", "education", "location"]);

// ─── Component ────────────────────────────────────────────────────────────────
const ScoreConfigPage = () => {
  const [criteria, setCriteria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null); // { type: 'success'|'error', msg }

  // Add-criterion form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newWeight, setNewWeight] = useState(0);
  const [addError, setAddError] = useState("");
  const [addLoading, setAddLoading] = useState(false);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Load config ─────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const data = await aiAutomationApi.get("/score-config");
        setCriteria(data.criteria || []);
      } catch (e) {
        showToast("error", "Failed to load score configuration.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Field updaters ──────────────────────────────────────────────────────────
  const updateField = useCallback((idx, field, value) => {
    setCriteria((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  }, []);

  const handleWeightChange = (idx, raw) => {
    updateField(idx, "weight", clamp(raw));
  };

  const handleToggle = (idx) => {
    setCriteria((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], is_active: !next[idx].is_active };
      return next;
    });
  };

  const handleDelete = async (criterion) => {
    if (!window.confirm(`Remove criterion "${criterion}"?`)) return;
    try {
      await aiAutomationApi.delete(`/score-config/criteria/${encodeURIComponent(criterion)}`);
      setCriteria((prev) => prev.filter((c) => c.criterion !== criterion));
      showToast("success", "Criterion removed.");
    } catch (e) {
      showToast("error", e?.detail || "Could not remove criterion.");
    }
  };

  // ── Save ────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const total = activeTotal(criteria);
    if (Math.abs(total - 100) > 0.5) {
      showToast("error", `Active weights must sum to 100. Currently: ${total.toFixed(1)}`);
      return;
    }
    setSaving(true);
    try {
      await aiAutomationApi.put("/score-config", { criteria });
      showToast("success", "Score configuration saved!");
    } catch (e) {
      showToast("error", e?.detail || "Failed to save configuration.");
    } finally {
      setSaving(false);
    }
  };

  // ── Add criterion ───────────────────────────────────────────────────────────
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setAddError("");
    if (!newLabel.trim()) {
      setAddError("Label is required.");
      return;
    }
    const key = newLabel.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
    if (criteria.some((c) => c.criterion === key)) {
      setAddError("A criterion with this name already exists.");
      return;
    }
    setAddLoading(true);
    try {
      const data = await aiAutomationApi.post("/score-config/criteria", {
        criterion: key,
        label: newLabel.trim(),
        weight: clamp(newWeight),
      });
      const added = data.criterion;
      setCriteria((prev) => [
        ...prev,
        {
          criterion: added.criterion,
          label: added.label,
          weight: Number(added.weight),
          is_active: added.is_active,
          sort_order: added.sort_order,
        },
      ]);
      setNewLabel("");
      setNewWeight(0);
      setShowAddForm(false);
      showToast("success", "Criterion added. Adjust weights and save.");
    } catch (e) {
      setAddError(e?.detail || "Failed to add criterion.");
    } finally {
      setAddLoading(false);
    }
  };

  // ── Derived ─────────────────────────────────────────────────────────────────
  const total = activeTotal(criteria);
  const totalOk = Math.abs(total - 100) <= 0.5;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen w-full flex bg-[#F6F7F9] dark:bg-gray-900">
      <main className="flex-1 min-h-screen bg-white dark:bg-gray-900 p-10 transition-colors duration-250">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-base font-semibold text-slate-800 dark:text-white flex items-center gap-2">
            <SliderIcon />
            Score Configuration
          </h2>
          <button
            onClick={() => { setShowAddForm((v) => !v); setAddError(""); }}
            className="text-sm px-4 py-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-700 hover:bg-indigo-100 dark:hover:bg-indigo-800 transition"
          >
            {showAddForm ? "Cancel" : "+ Add Criterion"}
          </button>
        </div>

        <p className="text-sm text-slate-500 dark:text-gray-400 mb-6">
          Configure how candidates are scored for your company. Active criteria weights must sum to{" "}
          <span className="font-semibold text-slate-700 dark:text-gray-200">100%</span>.
        </p>

        {/* Toast */}
        {toast && (
          <div
            className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium border ${
              toast.type === "success"
                ? "bg-green-50 border-green-200 text-green-800 dark:bg-green-900/30 dark:border-green-700 dark:text-green-200"
                : "bg-red-50 border-red-200 text-red-800 dark:bg-red-900/30 dark:border-red-700 dark:text-red-200"
            }`}
          >
            {toast.msg}
          </div>
        )}

        {/* Add criterion form */}
        {showAddForm && (
          <form
            onSubmit={handleAddSubmit}
            className="mb-6 p-5 rounded-xl border border-indigo-200 dark:border-indigo-700 bg-indigo-50/60 dark:bg-indigo-900/20"
          >
            <h3 className="text-sm font-semibold text-slate-700 dark:text-gray-200 mb-4">New Criterion</h3>
            <div className="flex flex-col sm:flex-row gap-3 items-start">
              <div className="flex-1">
                <label className="block text-xs text-slate-500 dark:text-gray-400 mb-1">Label</label>
                <input
                  type="text"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="e.g. Communication Skills"
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-slate-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
              <div className="w-32">
                <label className="block text-xs text-slate-500 dark:text-gray-400 mb-1">Initial Weight %</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={newWeight}
                  onChange={(e) => setNewWeight(clamp(e.target.value))}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-slate-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
              <div className="flex items-end pb-0.5">
                <button
                  type="submit"
                  disabled={addLoading}
                  className="px-5 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 transition"
                >
                  {addLoading ? "Adding…" : "Add"}
                </button>
              </div>
            </div>
            {addError && <p className="text-xs text-red-600 dark:text-red-400 mt-2">{addError}</p>}
          </form>
        )}

        {/* Criteria cards */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400 dark:text-gray-500 text-sm">
            Loading configuration…
          </div>
        ) : criteria.length === 0 ? (
          <div className="flex items-center justify-center py-20 text-slate-400 dark:text-gray-500 text-sm">
            No criteria found. Add one above.
          </div>
        ) : (
          <div className="space-y-4">
            {criteria.map((c, idx) => (
              <div
                key={c.criterion}
                className={`rounded-xl border p-5 transition-all duration-200 ${
                  c.is_active
                    ? "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                    : "border-gray-100 dark:border-gray-700/50 bg-gray-50 dark:bg-gray-800/50 opacity-60"
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  {/* Label */}
                  <input
                    type="text"
                    value={c.label}
                    onChange={(e) => updateField(idx, "label", e.target.value)}
                    className="text-sm font-semibold text-slate-700 dark:text-gray-200 bg-transparent border-b border-dashed border-gray-300 dark:border-gray-600 focus:outline-none focus:border-indigo-400 w-48 truncate"
                  />
                  <div className="flex items-center gap-3">
                    {/* Active toggle */}
                    <button
                      type="button"
                      onClick={() => handleToggle(idx)}
                      title={c.is_active ? "Disable criterion" : "Enable criterion"}
                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                        c.is_active ? "bg-indigo-500" : "bg-gray-300 dark:bg-gray-600"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ${
                          c.is_active ? "translate-x-4" : "translate-x-0"
                        }`}
                      />
                    </button>
                    {/* Delete (only non-builtin) */}
                    {!BUILTIN.has(c.criterion) && (
                      <button
                        type="button"
                        onClick={() => handleDelete(c.criterion)}
                        title="Remove criterion"
                        className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition"
                      >
                        <TrashIcon />
                      </button>
                    )}
                  </div>
                </div>

                {/* Slider row */}
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={c.weight}
                    disabled={!c.is_active}
                    onChange={(e) => handleWeightChange(idx, e.target.value)}
                    className="flex-1 h-2 rounded-lg appearance-none cursor-pointer accent-indigo-500 bg-gray-200 dark:bg-gray-600 disabled:cursor-not-allowed"
                  />
                  <div className="w-16 text-right">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={c.weight}
                      disabled={!c.is_active}
                      onChange={(e) => handleWeightChange(idx, e.target.value)}
                      className="w-14 px-2 py-1 text-sm text-center rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-slate-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-indigo-400 disabled:bg-gray-100 dark:disabled:bg-gray-700/50"
                    />
                  </div>
                  <span className="text-sm text-slate-500 dark:text-gray-400 w-3">%</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Total weight bar */}
        {!loading && criteria.length > 0 && (
          <div className="mt-6 p-4 rounded-xl border bg-gray-50 dark:bg-gray-800/60 border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-slate-600 dark:text-gray-300">Total active weight</span>
              <span
                className={`text-sm font-bold ${
                  totalOk ? "text-green-600 dark:text-green-400" : "text-red-500 dark:text-red-400"
                }`}
              >
                {total.toFixed(1)}% / 100%
              </span>
            </div>
            <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  totalOk
                    ? "bg-green-500 dark:bg-green-500"
                    : total > 100
                    ? "bg-red-500 dark:bg-red-500"
                    : "bg-amber-400 dark:bg-amber-400"
                }`}
                style={{ width: `${Math.min(total, 100)}%` }}
              />
            </div>
            {!totalOk && (
              <p className="text-xs text-red-500 dark:text-red-400 mt-1">
                {total > 100 ? `Over by ${(total - 100).toFixed(1)}%` : `Under by ${(100 - total).toFixed(1)}%`} — adjust
                sliders before saving.
              </p>
            )}
          </div>
        )}

        {/* Save button */}
        {!loading && criteria.length > 0 && (
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving || !totalOk}
              className="px-8 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-sm"
            >
              {saving ? "Saving…" : "Save Configuration"}
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default ScoreConfigPage;
